import { Box } from '@mui/material'
import type { ReactNode } from 'react'
import { RecordRepresentation, ReferenceField, type RaRecord, useRecordContext } from 'react-admin'

import { SYSTEM_PERMISSION_READ } from '../config/permissions'
import { useResourcePermissions } from '../domain/permissions'
import { getResourceByName } from '../resources'

type RecordTarget = (record: RaRecord | undefined) => ReactNode

const ReferenceValueTarget = ({ target }: { target: RecordTarget }) => {
  const record = useRecordContext()
  return target(record)
}

interface ReferenceValueProps {
  reference: string
  source?: string
  target?: RecordTarget
  emptyText?: string | null
}

type ResourceWithReferenceKey = { options?: { referenceKey?: string } }

const getReferenceKey = (reference: string) => {
  const resource = getResourceByName(reference) as ResourceWithReferenceKey | undefined
  return resource?.options?.referenceKey
}

const ReferenceValue = ({ reference, source, target, emptyText = 'n/a' }: ReferenceValueProps) => {
  const record = useRecordContext()
  const [hasReadForReference] = useResourcePermissions(reference, SYSTEM_PERMISSION_READ)
  const referenceSource = source ?? getReferenceKey(reference)

  if (!hasReadForReference) {
    return emptyText ?? ''
  }
  return (
    <Box display="inline-flex">
      <ReferenceField
        emptyText={emptyText ?? undefined}
        link={false}
        record={record}
        reference={reference}
        source={referenceSource ?? ''}
        sx={{
          '& .MuiTypography-root': {
            fontSize: 'inherit !important',
            fontWeight: 'inherit',
            fontFamily: 'inherit',
            lineHeight: 'inherit',
            letterSpacing: 'inherit',
          },
        }}
      >
        {target ? <ReferenceValueTarget target={target} /> : <RecordRepresentation />}
      </ReferenceField>
    </Box>
  )
}

export default ReferenceValue
